'use client';
import { RadioGroup } from '@headlessui/react';
import { clsx } from 'clsx';
import Image from 'next/image';

import { RadioButtonVariant } from './radio.types';

import type { RadioButtonProps } from './radio.types';

const RadioButton = ({
  option,
  variant = RadioButtonVariant.LEFT,
}: RadioButtonProps) => {
  return (
    <RadioGroup.Option
      className={clsx(
        variant === RadioButtonVariant.LEFT
          ? 'flex-row'
          : 'flex-row-reverse place-content-between justify-end',
        'group/radio relative flex cursor-pointer items-center gap-b focus:outline-none aria-disabled:pointer-events-none'
      )}
      value={option.id}
      {...(option.disabled ? { disabled: option.disabled } : {})}
    >
      {({ checked, disabled }) => (
        <>
          <span
            aria-hidden="true"
            className="flex h-[1.5rem] w-[1.5rem] shrink-0 items-center justify-center rounded-full border-2 border-radio-border-default bg-radio-background-default transition-colors group-focus-visible/radio:border-radio-border-focus group-active/radio:border-radio-border-pressed group-active/radio:bg-radio-background-pressed group-aria-disabled/radio:border-radio-border-disabled"
          >
            <span
              className={clsx(
                checked
                  ? 'bg-radio-background-checked'
                  : 'bg-radio-background-default',
                checked && disabled && 'bg-radio-background-disabled',
                'h-[1rem] w-[1rem] rounded-full transition-colors group-active/radio:bg-radio-background-pressed'
              )}
            />
          </span>
          <span
            className={clsx(
              variant === RadioButtonVariant.RIGHT && 'flex-1',
              'flex flex-col',
              {
                'w-full': option.image,
              }
            )}
          >
            <span
              className={clsx('flex items-center gap-b', {
                'flex-row-reverse justify-end':
                  variant === RadioButtonVariant.RIGHT && option.image,
                'w-full justify-between':
                  variant === RadioButtonVariant.LEFT && option.image,
              })}
            >
              <RadioGroup.Label
                as="span"
                className={clsx(
                  checked
                    ? 'text-radio-label-checked'
                    : 'text-radio-label-default',
                  'text-h4 block transition-colors group-hover/radio:text-radio-label-hover group-active/radio:text-radio-label-pressed group-aria-disabled/radio:text-radio-label-disabled'
                )}
              >
                {option.name}
              </RadioGroup.Label>
              {option.image ? (
                <span className="flex h-[4rem] w-[6.25rem] items-center justify-center rounded-rad-b border border-gray-300 bg-gray-50 p-a">
                  <Image
                    alt={option.image.alt}
                    src={option.image.src}
                    width={100}
                    height={100}
                    className="h-full w-full object-contain object-center"
                  />
                </span>
              ) : null}
            </span>
            {option.description ? (
              <RadioGroup.Description
                as="span"
                className="text-body2 block text-radio-label-default group-aria-disabled/radio:text-radio-label-disabled"
              >
                {option.description}
              </RadioGroup.Description>
            ) : null}
          </span>
        </>
      )}
    </RadioGroup.Option>
  );
};

export default RadioButton;
